/**
 * The moderation taxonomy the screening pipeline and the admin review tools
 * share: which surface content came from, how severe the verdict was, which
 * guideline it matched, which pipeline stage produced it, and what an admin can
 * decide about it.
 *
 * Every one of these reaches the client as a **string** name — the API
 * registers a global `JsonStringEnumConverter` — but the database, the stored
 * procedures and the handoff notes all speak in ordinals, so each reader
 * accepts both. The ordinals are 1-based and follow the declaration order in
 * `swagger.json`; they are identifiers, never a scale to compare with `<`.
 */

import { createModerationEnumReader } from './moderationEnum';

/** Where the content was published. `ContentModerationSurface` in swagger. */
export const MODERATION_SURFACE = {
  post: 'Post',
  comment: 'Comment',
  chat: 'Chat',
  profile: 'Profile',
} as const;

export type ModerationSurface =
  (typeof MODERATION_SURFACE)[keyof typeof MODERATION_SURFACE];

/**
 * How urgent the verdict is. `Urgent` is what the queue sorts to the front;
 * `None` belongs to rows that were screened without matching anything.
 */
export const MODERATION_SEVERITY = {
  none: 'None',
  heldForReview: 'HeldForReview',
  urgent: 'Urgent',
} as const;

export type ModerationSeverity =
  (typeof MODERATION_SEVERITY)[keyof typeof MODERATION_SEVERITY];

/** The community guideline a match was scored against. */
export const MODERATION_CATEGORY = {
  bullyingBodyShaming: 'BullyingBodyShaming',
  dangerousWeightLoss: 'DangerousWeightLoss',
  unsafeMedicalAdvice: 'UnsafeMedicalAdvice',
  selfHarmOrThreats: 'SelfHarmOrThreats',
  hateDiscrimination: 'HateDiscrimination',
  sexualContentHarassment: 'SexualContentHarassment',
  privacyImpersonation: 'PrivacyImpersonation',
  spamScamsSelling: 'SpamScamsSelling',
  obsceneContent: 'ObsceneContent',
  politicalContent: 'PoliticalContent',
  religiousDisputes: 'ReligiousDisputes',
  unwantedContact: 'UnwantedContact',
  promotionSolicitation: 'PromotionSolicitation',
  consentViolation: 'ConsentViolation',
  childSafety: 'ChildSafety',
} as const;

export type ModerationCategory =
  (typeof MODERATION_CATEGORY)[keyof typeof MODERATION_CATEGORY];

/** The pipeline stage that produced a verdict. */
export const MODERATION_STAGE = {
  localDetectors: 'LocalDetectors',
  omniModeration: 'OmniModeration',
  policyClassifier: 'PolicyClassifier',
} as const;

export type ModerationStage =
  (typeof MODERATION_STAGE)[keyof typeof MODERATION_STAGE];

/**
 * What was screened. Four of these are media, and three of those four are
 * attachments to authored content, so a ruling on one of those lands on the
 * post, comment or message carrying it rather than on the media row itself. A
 * profile picture is media with no such carrier: its ruling lands on the
 * picture, which is why the two cases are separate predicates below.
 */
export const MODERATION_TARGET_TYPE = {
  post: 'Post',
  postComment: 'PostComment',
  message: 'Message',
  postMedia: 'PostMedia',
  postCommentMedia: 'PostCommentMedia',
  messageAttachment: 'MessageAttachment',
  profilePicture: 'ProfilePicture',
} as const;

export type ModerationTargetType =
  (typeof MODERATION_TARGET_TYPE)[keyof typeof MODERATION_TARGET_TYPE];

/**
 * An admin's ruling. `ApplyReview` has no default for it on purpose: an omitted
 * value arrives as the ordinal 0 and is rejected, rather than silently allowing
 * or rejecting the content.
 */
export const MODERATION_REVIEW_ACTION = {
  allow: 'Allow',
  reject: 'Reject',
} as const;

export type ModerationReviewAction =
  (typeof MODERATION_REVIEW_ACTION)[keyof typeof MODERATION_REVIEW_ACTION];

export const readModerationSurface = createModerationEnumReader(
  Object.values(MODERATION_SURFACE),
);
export const readModerationSeverity = createModerationEnumReader(
  Object.values(MODERATION_SEVERITY),
);
export const readModerationCategory = createModerationEnumReader(
  Object.values(MODERATION_CATEGORY),
);
export const readModerationStage = createModerationEnumReader(
  Object.values(MODERATION_STAGE),
);
export const readModerationTargetType = createModerationEnumReader(
  Object.values(MODERATION_TARGET_TYPE),
);
export const readModerationReviewAction = createModerationEnumReader(
  Object.values(MODERATION_REVIEW_ACTION),
);

const MEDIA_TARGET_TYPES: readonly ModerationTargetType[] = [
  MODERATION_TARGET_TYPE.postMedia,
  MODERATION_TARGET_TYPE.postCommentMedia,
  MODERATION_TARGET_TYPE.messageAttachment,
  MODERATION_TARGET_TYPE.profilePicture,
];

/**
 * Media attached to authored content. A profile picture is excluded: it is
 * media, but it hangs off a member rather than off a post, comment or message,
 * so it has no parent row for a ruling to move to.
 */
const PARENT_BEARING_TARGET_TYPES: readonly ModerationTargetType[] = [
  MODERATION_TARGET_TYPE.postMedia,
  MODERATION_TARGET_TYPE.postCommentMedia,
  MODERATION_TARGET_TYPE.messageAttachment,
];

/** True when the target is media rather than authored text. */
export const isModerationMediaTarget = (targetType: unknown): boolean => {
  const normalized = readModerationTargetType(targetType);
  return normalized !== null && MEDIA_TARGET_TYPES.includes(normalized);
};

/**
 * True when a ruling lands on the content carrying the target rather than on
 * the target itself, which is why a decision can change a row the operator did
 * not click on. Says nothing about whether the target is media — a profile
 * picture is media and still answers `false`.
 */
export const hasModerationParentTarget = (targetType: unknown): boolean => {
  const normalized = readModerationTargetType(targetType);
  return normalized !== null && PARENT_BEARING_TARGET_TYPES.includes(normalized);
};
